import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { TSavedViewsAgGridProps as Props } from '../SavedViewsStateful';
import { IData } from '../SavedViews/SavedViews';

export const defaultColumnPaths = ['colId', 'pinned', 'sort', 'sortIndex', 'hide'];
export const defaultPaginationPaths = ['pageSize'];

type TColumn = Record<string, any>;

export const defaultComparator = ({
  initialData,
  newData,
}: {
  initialData: IData;
  newData: IData;
}) => {
  const initialColumns = initialData.columns?.map((c: TColumn) => pick(c, defaultColumnPaths));
  const newColumns = newData.columns?.map((c: TColumn) => pick(c, defaultColumnPaths));

  const initialPagination = pick(initialData.pagination, defaultPaginationPaths);
  const newPagination = pick(newData.pagination, defaultPaginationPaths);

  return (
    !isEqual(initialColumns, newColumns) ||
    !isEqual(initialData.filters, newData.filters) ||
    !isEqual(initialPagination, newPagination)
  );
};

export const getDefaultFetchSavedViews =
  (key: string): NonNullable<Props['onFetchSavedViews']> =>
  () =>
    Promise.resolve(localStorage.getItem(key))
      .then((viewString) => (viewString ? JSON.parse(viewString) : null))
      .then((data) => data || []);

export const getDefaultSaveSavedView =
  (key: string): NonNullable<Props['onSaveSavedView']> =>
  ({ data, view, views }) => {
    const newViews = views.map((v) => {
      if (v.id === view.id) {
        return { ...v, data };
      }
      return v;
    });
    localStorage.setItem(key, JSON.stringify(newViews));
    return Promise.resolve(newViews);
  };

export const getDefaultCreateSavedView =
  (key: string): NonNullable<Props['onCreateSavedView']> =>
  ({ data, name, views }) => {
    const newViews = views
      .map((v) => ({ ...v, isActive: false }))
      .concat({
        id: `saved-view-${Date.now()}`,
        name,
        isActive: true,
        data,
      });
    localStorage.setItem(key, JSON.stringify(newViews));
    return Promise.resolve(newViews);
  };

export const getDefaultSetDefaultSavedView =
  (key: string): NonNullable<Props['onSetDefaultSavedView']> =>
  ({ view, views }) => {
    const newViews = views.map((v) => ({ ...v, isDefault: v.id === view.id }));
    localStorage.setItem(key, JSON.stringify(newViews));
    return Promise.resolve(newViews);
  };

export const getDefaultRenameSavedView =
  (key: string): NonNullable<Props['onRenameSavedView']> =>
  ({ name, view, views }) => {
    const newViews = views.map((v) => ({ ...v, name: v.id === view.id ? name : v.name }));
    localStorage.setItem(key, JSON.stringify(newViews));
    return Promise.resolve(newViews);
  };

export const getDefaultDeleteSavedView =
  (key: string): NonNullable<Props['onDeleteSavedView']> =>
  ({ view, views }) => {
    const newViews = views.filter((v) => v.id !== view.id);
    const newActiveView =
      newViews.find((v) => v.isActive) || newViews.find((v) => v.isDefault) || newViews[0];
    const newActiveViewIndex = newViews.findIndex((v) => v.id === newActiveView.id);
    const storageViews = [
      ...newViews.slice(0, newActiveViewIndex),
      { ...newActiveView, isActive: true },
      ...newViews.slice(newActiveViewIndex + 1),
    ];
    localStorage.setItem(key, JSON.stringify(storageViews));
    return Promise.resolve(newViews);
  };

export const getDefaultSetActiveSavedView =
  (key: string): NonNullable<Props['onSetActiveSavedView']> =>
  ({ view, views }) => {
    const newViews = views.map((v) => ({ ...v, isActive: v.id === view.id }));
    localStorage.setItem(key, JSON.stringify(newViews));
    return Promise.resolve(newViews);
  };
